/* BatchPilotRunner — upload a ZIP of 50 radiographs + CSV of Dr. Ivanova's
 * verdicts, trigger server-side fan-out through GPT-5.2 Vision, and poll
 * the resulting κ back into the Inter-rater panel.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, FileArchive, FileText, RefreshCw, Trash2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const STATUS_STYLE = {
  pending:           { color: "#6B7280", icon: Clock,          label: "чакащ" },
  running:           { color: "#0EA5E9", icon: Loader2,        label: "обработва" },
  done:              { color: "#10B981", icon: CheckCircle2,   label: "готов" },
  done_with_errors:  { color: "#F59E0B", icon: AlertTriangle,  label: "готов (с грешки)" },
  error:             { color: "#EF4444", icon: AlertTriangle,  label: "грешка" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${s.color}15`, color: s.color }} data-testid={`batch-status-${status}`}>
      <Icon className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} /> {s.label}
    </span>
  );
}

export default function BatchPilotRunner({ API, isDark, onJobComplete }) {
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    label: "",
    clinician_name: "Д-р Лили Иванова",
    clinician_role: "dentist",
  });
  const zipRef = useRef(null);
  const csvRef = useRef(null);

  const token = useMemo(
    () => localStorage.getItem("token") || sessionStorage.getItem("nutramilo_access_token") || "",
    []
  );
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/xray-batch/jobs`, { headers: auth });
      setJobs(data.jobs || []);
    } catch {
      toast.error("Грешка при зареждане на jobs");
    } finally { setLoading(false); }
  }, [API, auth]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll running jobs every 3s so progress moves without manual refresh
  useEffect(() => {
    const hasRunning = jobs.some(j => j.status === "pending" || j.status === "running");
    if (!hasRunning) return undefined;
    const id = setInterval(loadJobs, 3000);
    return () => clearInterval(id);
  }, [jobs, loadJobs]);

  const openJob = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/xray-batch/job/${id}`, { headers: auth });
      setActiveJob(data);
    } catch { toast.error("Грешка при отваряне на job"); }
  };

  const submit = async () => {
    const zipFile = zipRef.current?.files?.[0];
    const csvFile = csvRef.current?.files?.[0];
    if (!form.label.trim() || !form.clinician_name.trim() || !zipFile || !csvFile) {
      toast.error("Label, клиницист, ZIP и CSV са задължителни");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("label", form.label);
    fd.append("clinician_name", form.clinician_name);
    fd.append("clinician_role", form.clinician_role);
    fd.append("zip_file", zipFile);
    fd.append("verdict_csv", csvFile);
    try {
      const { data } = await axios.post(`${API}/admin/xray-batch/upload`, fd, {
        headers: { ...auth, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Job създаден · ${data.job_id.slice(0, 8)}…`);
      if (zipRef.current) zipRef.current.value = "";
      if (csvRef.current) csvRef.current.value = "";
      setForm(f => ({ ...f, label: "" }));
      loadJobs();
      openJob(data.job_id);
      onJobComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при upload");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Изтриване на този job и всичките му inter-rater записи?")) return;
    try {
      const { data } = await axios.delete(`${API}/admin/xray-batch/job/${id}`, { headers: auth });
      toast.success(`Изтрито · ${data.entries_deleted} записа`);
      if (activeJob?.id === id) setActiveJob(null);
      loadJobs();
      onJobComplete?.();
    } catch { toast.error("Грешка"); }
  };

  return (
    <div className="space-y-4" data-testid="batch-pilot-runner">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Bulk X-ray pilot runner</h3>
          <p className="text-xs text-gray-500">ZIP ({"<"}= 50 снимки) + CSV verdict-и → fan-out през GPT-5.2 Vision → автоматично изчисляване на κ</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadJobs} disabled={loading} data-testid="batch-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Опресни
        </Button>
      </div>

      {/* Upload form */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""} data-testid="batch-upload-form">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold flex items-center gap-2"><Upload className="w-4 h-4" /> Нов batch job</h4>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-gray-500">Label</label>
              <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="напр. Ivanova-Pilot-May-2026" className="h-9 text-sm" data-testid="batch-label" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Клиницист (име)</label>
              <Input value={form.clinician_name} onChange={e => setForm(f => ({ ...f, clinician_name: e.target.value }))}
                className="h-9 text-sm" data-testid="batch-clin-name" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Роля</label>
              <select value={form.clinician_role} onChange={e => setForm(f => ({ ...f, clinician_role: e.target.value }))}
                className="w-full h-9 text-sm border rounded px-2" data-testid="batch-clin-role">
                <option value="dentist">dentist</option>
                <option value="radiologist">radiologist</option>
                <option value="oncologist">oncologist</option>
                <option value="gp">GP</option>
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-gray-500 flex items-center gap-1"><FileArchive className="w-3 h-3" /> ZIP със снимки</label>
              <input ref={zipRef} type="file" accept=".zip,application/zip,application/x-zip-compressed"
                className="w-full text-xs border rounded px-2 py-1.5" data-testid="batch-zip" />
              <p className="text-[10px] text-gray-400 mt-1">case_001.jpg … case_050.jpg (max 200 MB)</p>
            </div>
            <div>
              <label className="text-[11px] text-gray-500 flex items-center gap-1"><FileText className="w-3 h-3" /> CSV verdict-и</label>
              <input ref={csvRef} type="file" accept=".csv,text/csv"
                className="w-full text-xs border rounded px-2 py-1.5" data-testid="batch-csv" />
              <p className="text-[10px] text-gray-400 mt-1">колони: case_id, verdict (0/1/2), confidence, notes</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="batch-submit">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
              Стартирай pilot
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Jobs list */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3">История на jobs ({jobs.length})</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1">Дата</th>
                  <th className="text-left">Label</th>
                  <th>Статус</th>
                  <th>Прогрес</th>
                  <th>Agreed</th>
                  <th>Disagreed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-400 py-6">Все още няма jobs</td></tr>
                )}
                {jobs.map(j => (
                  <tr key={j.id} className={`border-t cursor-pointer ${j.id === activeJob?.id ? "bg-sky-50/50" : ""}`}
                    onClick={() => openJob(j.id)} data-testid={`batch-job-${j.id}`}>
                    <td className="py-1.5 text-gray-500">{new Date(j.created_at).toLocaleString("bg-BG")}</td>
                    <td className="font-medium">{j.label}</td>
                    <td className="text-center"><StatusPill status={j.status} /></td>
                    <td className="text-center">{j.processed ?? 0} / {j.total ?? 0}</td>
                    <td className="text-center text-green-600">{j.agreed ?? 0}</td>
                    <td className="text-center text-amber-600">{j.disagreed ?? 0}</td>
                    <td className="text-right">
                      <button onClick={(e) => { e.stopPropagation(); del(j.id); }}
                        className="text-red-500 hover:text-red-700" data-testid={`batch-del-${j.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Active job detail — per-case breakdown */}
      {activeJob && (
        <Card className={isDark ? "bg-gray-800 border-gray-700" : ""} data-testid="batch-active-detail">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h4 className="font-semibold">{activeJob.label}</h4>
                <p className="text-xs text-gray-500">
                  {activeJob.clinician_name} · {activeJob.clinician_role} · {activeJob.processed}/{activeJob.total} обработени
                </p>
              </div>
              <StatusPill status={activeJob.status} />
            </div>
            {activeJob.error && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{activeJob.error}</div>
            )}
            {(activeJob.skipped_no_verdict?.length || activeJob.skipped_no_image?.length) ? (
              <div className="text-[11px] text-gray-500">
                {activeJob.skipped_no_verdict?.length > 0 && <>Пропуснати (няма verdict): {activeJob.skipped_no_verdict.join(", ")}<br /></>}
                {activeJob.skipped_no_image?.length > 0 && <>Пропуснати (няма снимка): {activeJob.skipped_no_image.join(", ")}</>}
              </div>
            ) : null}
            {activeJob.cases && activeJob.cases.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="text-left py-1">Case</th>
                      <th>AI</th>
                      <th>Clin</th>
                      <th>Съгл.</th>
                      <th>AI причина</th>
                      <th>Увереност</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeJob.cases.map(c => {
                      const agree = c.ai_label === c.clinician_label;
                      return (
                        <tr key={c.case_ref} className="border-t" data-testid={`batch-case-${c.case_ref}`}>
                          <td className="py-1 font-mono">{c.case_ref}</td>
                          <td className="text-center">{c.ai_label}</td>
                          <td className="text-center">{c.clinician_label}</td>
                          <td className="text-center">
                            {agree ? <CheckCircle2 className="w-3 h-3 inline text-green-600" /> : <AlertTriangle className="w-3 h-3 inline text-amber-600" />}
                          </td>
                          <td className="text-[10px] text-gray-500">{c.ai_derivation_reason}</td>
                          <td className="text-center text-gray-500">{c.clinician_confidence ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
